import { useState } from 'react';
import ImageUploading from 'react-images-uploading';
import DeleteIcon from 'src/assets/all-icons/delete-icon.jsx';
import { v4 as uuidv4 } from 'uuid';

import config from '@/config';
import useTranslations from '@/i18n/useTranslations';
import PopupUpdateArticleCover from '@/pages/artwork/artwork-header-mobile/options/download-template/components/PopupUpdateArticleCover';
import UserService from '@/services/user.service';
import ArtworkImage from '@/shared-components/cards/components/ArtworkImage';
import Text from '@/shared-components/text/components/Text';

import { normalizeCoverImage } from '../galleryCoverImage.utils';

import ProfileCoverPickerPopup from './ProfileCoverPickerPopup';

import './styles/ProfileCoverImage.css';

const GALLERY_COVER_ASPECT_RATIO = 3 / 2;

export default function ProfileCoverImage({
  userId,
  userType = 'gallery',
  coverField = 'gallery_cover_image',
  formState,
  setFormState,
  onCoverChange,
  disableCoverActions = false
}) {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [openCoverPicker, setOpenCoverPicker] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const t = useTranslations('common.user_settings.edit_profile');
  const currentCover = formState[coverField];
  const actionsDisabled = disableCoverActions || Boolean(currentCover?.loading);
  const coverImageId = currentCover?.image_id;
  const closeCropPopup = () => {
    setIsPopupOpen(false);
    setImageToCrop(null);
  };

  async function uploadCoverImage(image) {
    if (!image || actionsDisabled) {
      return;
    }

    setImageToCrop(image);
    setIsPopupOpen(true);
  }

  async function handleCroppedCover(croppedFile) {
    if (!croppedFile || actionsDisabled) {
      return;
    }

    const temporalImageId = uuidv4();

    setFormState((prevValue) => ({
      ...prevValue,
      [coverField]: {
        loading: true,
        temporalImageId,
        image_original_data: {
          name: croppedFile.name
        }
      }
    }));

    const { response, data } = await UserService.uploadImage(croppedFile);
    const normalizedCoverImage = normalizeCoverImage(data);

    if (response.ok && normalizedCoverImage) {
      setFormState((prevValue) => {
        if (prevValue[coverField]?.temporalImageId === temporalImageId) {
          return {
            ...prevValue,
            [coverField]: normalizedCoverImage
          };
        }
        return prevValue;
      });

      if (typeof onCoverChange === 'function') {
        onCoverChange(normalizedCoverImage);
      }
    } else {
      setFormState((prevValue) => ({
        ...prevValue,
        [coverField]: null
      }));

      alert(t('alerts.generic_error_contact', { email: 'contact@suarte.art' }));
    }
  }

  function handleDeleteImage() {
    if (actionsDisabled) {
      return;
    }

    setFormState((prevValue) => ({
      ...prevValue,
      [coverField]: null
    }));

    if (typeof onCoverChange === 'function') {
      onCoverChange(null);
    }
  }

  function handleCoverPictureError(errors) {
    if (errors.maxNumber) alert(t('profile_image.errors.max_number'));
    else if (errors.acceptType) alert(t('profile_image.errors.invalid_type'));
    else if (errors.maxFileSize) alert(t('profile_image.errors.max_file_size'));
  }

  function startCropFromArtwork(nextCoverImage) {
    if (actionsDisabled) {
      return;
    }

    const normalizedCoverImage = normalizeCoverImage(nextCoverImage);
    if (!normalizedCoverImage) {
      return;
    }

    setOpenCoverPicker(false);
    setImageToCrop(`${config.app.imageServiceDomain}/${normalizedCoverImage.image_id}/public`);
    setIsPopupOpen(true);
  }

  return (
    <ImageUploading
      maxNumber={1}
      onChange={(imagesList) => uploadCoverImage(imagesList?.[0]?.file)}
      onError={handleCoverPictureError}
      maxFileSize={15000000}
    >
      {({ onImageUpload }) => (
        <div className="profile-cover-image__block">
          <div className="profile-cover-image__container">
            <div
              className="profile-cover-image__preview"
              onClick={() => { if (!actionsDisabled) onImageUpload(); }}
              role="button"
              tabIndex={actionsDisabled ? -1 : 0}
              onKeyDown={(event) => {
                if (!actionsDisabled && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault();
                  onImageUpload();
                }
              }}
            >
              {coverImageId ? (
                <ArtworkImage
                  image={coverImageId}
                  imageClassName="profile-cover-image__image"
                  imageTemplateClassName="profile-cover-image__image"
                />
              ) : (
                <div className="profile-cover-image__placeholder">
                  <Text>Add cover image</Text>
                </div>
              )}
            </div>

            {Boolean(currentCover) && (
              <DeleteIcon className="profile-cover-image__delete-image" onClick={handleDeleteImage} />
            )}
          </div>

          <div className="profile-cover-image__actions-block">
            <Text
              className="profile-cover-image__upload-image element-non-selectable"
              onClick={() => { if (!actionsDisabled) setOpenCoverPicker(true); }}
            >
              {currentCover ? 'Change cover' : 'Add cover'}
            </Text>
          </div>

          {openCoverPicker && (
            <ProfileCoverPickerPopup
              open={openCoverPicker}
              close={() => setOpenCoverPicker(false)}
              userId={userId}
              userType={userType}
              selectedImageId={currentCover?.image_id}
              onAddImage={() => {
                setOpenCoverPicker(false);
                if (!actionsDisabled) onImageUpload();
              }}
              onSelectArtworkImage={(imageData) => {
                startCropFromArtwork(imageData);
              }}
            />
          )}

          {isPopupOpen && imageToCrop && (
            <PopupUpdateArticleCover
              open={isPopupOpen}
              image={imageToCrop}
              exitCrop={closeCropPopup}
              continueCrop={closeCropPopup}
              setCroppedFile={handleCroppedCover}
              headerText="Update cover image"
              instructionsText="Adjust and crop your cover image"
              continueLabel="Set cover image"
              aspect={GALLERY_COVER_ASPECT_RATIO}
            />
          )}
        </div>
      )}
    </ImageUploading>
  );
}
